import type { QueryClient } from '@tanstack/react-query';

import { isResolvedSiteTimelineQuery } from '@/domain/fishGroups/siteTimelineQuery';
import {
    ClientController_Overview,
    LocationFishGroupsController_occupiedPens,
    LocationFishGroupsController_timeline,
    StockingCyclesController_list,
} from '@/services/services';

/**
 * Refetches what the fish group board reads after a write: the cycle list (status is derived
 * from the groups, so a planned cycle turns active), every resolved timeline scope, the
 * occupied pens, and the client overview; the last two decide which pens the create form
 * offers. With `locationId`, only that site's board; without it, every site of the client,
 * for the internal writes, which don't know the site. Only boards in the cache are touched.
 *
 * `refetchType: 'all'`, because the app sets `refetchOnMount: false`: a timeline scope that
 * isn't on screen would otherwise stay stale when the customer switches back to it. That is
 * also why the timeline invalidation is narrowed to resolved scopes: the entry the board
 * registers before it has a scope would otherwise be fetched without one.
 */
export async function invalidateSiteBoardQueries(
    queryClient: QueryClient,
    clientId: number,
    locationId?: number
): Promise<void> {
    const scope = locationId === undefined ? [clientId] : [clientId, locationId];
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: [StockingCyclesController_list.key, ...scope],
            refetchType: 'all',
        }),
        queryClient.invalidateQueries({
            queryKey: [LocationFishGroupsController_timeline.key, ...scope],
            predicate: isResolvedSiteTimelineQuery,
            refetchType: 'all',
        }),
        queryClient.invalidateQueries({
            queryKey: [LocationFishGroupsController_occupiedPens.key, ...scope],
            refetchType: 'all',
        }),
        queryClient.invalidateQueries({
            queryKey: [ClientController_Overview.key, clientId],
            refetchType: 'all',
        }),
    ]);
}
